import React from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Repeat as RepeatIcon, DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import { DAY_OF_MONTH_OPTIONS } from '../../constants/scheduledReportTypes';

export type RecurrenceUnit = 'day' | 'week' | 'month';

export interface ReportScheduleRecurrenceValue {
  frequencyType: RecurrenceUnit;
  frequencyInterval: number;
  frequencyDayOfMonth: number;
  daysOfWeek: number[];
  stopDate: string;
}

interface ReportScheduleRecurrenceFieldsProps {
  value: ReportScheduleRecurrenceValue;
  onChange: (value: ReportScheduleRecurrenceValue) => void;
  onClear?: () => void;
}

const TEAMS_WEEKDAY_OPTIONS = [
  { value: 1, label: 'M' },
  { value: 2, label: 'T' },
  { value: 3, label: 'W' },
  { value: 4, label: 'T' },
  { value: 5, label: 'F' },
  { value: 6, label: 'S' },
  { value: 0, label: 'S' },
] as const;

const UNIT_LABELS: Record<RecurrenceUnit, { single: string; plural: string }> = {
  day: { single: 'day', plural: 'days' },
  week: { single: 'week', plural: 'weeks' },
  month: { single: 'month', plural: 'months' },
};

export function formatRecurrenceSummary(value: ReportScheduleRecurrenceValue): string {
  const interval = Math.max(1, value.frequencyInterval || 1);
  const unit = UNIT_LABELS[value.frequencyType];
  const unitLabel = interval === 1 ? unit.single : unit.plural;
  let summary = `Every ${interval} ${unitLabel}`;

  if (value.frequencyType === 'week') {
    const labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const days = value.daysOfWeek.length > 0
      ? value.daysOfWeek.map((day) => labels[day]).join(', ')
      : 'Mon';
    summary += ` on ${days}`;
  }

  if (value.frequencyType === 'month') {
    const dom = value.frequencyDayOfMonth;
    summary += dom === -1 ? ' on last day' : ` on day ${dom}`;
  }

  if (value.frequencyType === 'day' && value.daysOfWeek.length > 0) {
    const labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    summary += ` (${value.daysOfWeek.map((day) => labels[day]).join(', ')})`;
  }

  if (value.stopDate) {
    summary += ` until ${value.stopDate}`;
  }

  return summary;
}

const ReportScheduleRecurrenceFields: React.FC<ReportScheduleRecurrenceFieldsProps> = ({
  value,
  onChange,
  onClear,
}) => {
  const patch = (partial: Partial<ReportScheduleRecurrenceValue>) => onChange({ ...value, ...partial });

  const toggleWeekday = (dayValue: number) => {
    const exists = value.daysOfWeek.includes(dayValue);
    const daysOfWeek = exists
      ? value.daysOfWeek.filter((day) => day !== dayValue)
      : [...value.daysOfWeek, dayValue].sort((a, b) => a - b);
    patch({ daysOfWeek });
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <RepeatIcon fontSize="small" color="action" />
        <Typography variant="subtitle2">Recurrence</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">Repeat every</Typography>
        <TextField
          type="number"
          size="small"
          value={value.frequencyInterval}
          onChange={(e) => patch({ frequencyInterval: Math.max(1, Number(e.target.value) || 1) })}
          inputProps={{ min: 1, max: 99, style: { width: 56, textAlign: 'center' } }}
          sx={{ width: 80 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Unit</InputLabel>
          <Select
            value={value.frequencyType}
            label="Unit"
            onChange={(e) => {
              const frequencyType = e.target.value as RecurrenceUnit;
              patch({
                frequencyType,
                daysOfWeek: frequencyType === 'week' && value.daysOfWeek.length === 0 ? [1] : value.daysOfWeek,
              });
            }}
          >
            <MenuItem value="day">day</MenuItem>
            <MenuItem value="week">week</MenuItem>
            <MenuItem value="month">month</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {value.frequencyType === 'week' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {TEAMS_WEEKDAY_OPTIONS.map((day, index) => {
              const selected = value.daysOfWeek.includes(day.value);
              return (
                <Box
                  key={`${day.value}-${index}`}
                  component="button"
                  type="button"
                  onClick={() => toggleWeekday(day.value)}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    bgcolor: selected ? 'primary.main' : 'grey.200',
                    color: selected ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      bgcolor: selected ? 'primary.dark' : 'grey.300',
                    },
                  }}
                >
                  {day.label}
                </Box>
              );
            })}
          </Box>
          <Typography variant="body2" color="text.secondary">Until</Typography>
          <TextField
            type="date"
            size="small"
            value={value.stopDate}
            onChange={(e) => patch({ stopDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            placeholder="No end date"
            sx={{ minWidth: 180 }}
          />
          {onClear && value.stopDate && (
            <IconButton size="small" onClick={onClear} aria-label="Clear recurrence end date">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {value.frequencyType === 'day' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Optional: limit to specific days
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {TEAMS_WEEKDAY_OPTIONS.map((day, index) => {
              const selected = value.daysOfWeek.includes(day.value);
              return (
                <Box
                  key={`day-filter-${day.value}-${index}`}
                  component="button"
                  type="button"
                  onClick={() => toggleWeekday(day.value)}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    bgcolor: selected ? 'primary.main' : 'grey.200',
                    color: selected ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  {day.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {value.frequencyType === 'month' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">On</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Day of month</InputLabel>
            <Select
              value={value.frequencyDayOfMonth}
              label="Day of month"
              onChange={(e) => patch({ frequencyDayOfMonth: Number(e.target.value) })}
            >
              {DAY_OF_MONTH_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        {value.frequencyType !== 'week' && (
          <>
            <Typography variant="body2" color="text.secondary">Until</Typography>
            <TextField
              type="date"
              size="small"
              value={value.stopDate}
              onChange={(e) => patch({ stopDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              placeholder="No end date"
              sx={{ minWidth: 180 }}
            />
            {onClear && value.stopDate && (
              <IconButton size="small" onClick={onClear} aria-label="Clear recurrence end date">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: value.frequencyType === 'week' ? 0 : 'auto', width: value.frequencyType === 'week' ? '100%' : undefined, mt: value.frequencyType === 'week' ? 0.5 : 0 }}>
          {formatRecurrenceSummary(value)}
        </Typography>
      </Box>
    </Box>
  );
};

export default ReportScheduleRecurrenceFields;
